import { Data } from "@core/content/types";
import {
  configureCreatorContentStore,
  getCreatorPayload,
  initializeCreatorData as initializeCreatorDataCore,
} from "./contentStore";
import { type ImageTargetDescriptor } from "@webcore/core/targets/target.image.8w";
import {
  COLLECTION_DATA_SOURCE,
  getActiveArtworkConfig,
  setActiveArtworkById,
  SIGN_LABEL,
  SIGN_TO_ARTWORK_ID,
  type SignKey,
} from "./artworks";
import {
  assertCollectionCached,
  getCacheProvenance,
  getCachedArtworkIds,
  getLocalCreatorPayload,
  getLocalImageTarget,
} from "./localCollection";

export {
  ContentData,
  MicrositeData,
  INITIAL_CONTENT_ID,
  getContent,
} from "./contentStore";

/**
 * The cached target for one artwork, or a failure that names the fix.
 *
 * Targets are packed ahead of time by `pnpm run cache:collection` rather than
 * generated in the browser, so a missing one means the cache is stale — an
 * artwork was added in Creator and never pulled down.
 */
const requireLocalTarget = (artworkId: string): ImageTargetDescriptor => {
  const target = getLocalImageTarget(artworkId);
  if (target) return target;

  throw new Error(
    `No cached image target for "${artworkId}".\n` +
      `The cache holds: ${getCachedArtworkIds().join(", ") || "(nothing)"}.\n` +
      `If this artwork is new in Creator, refresh the cache and rebuild:\n\n` +
      `    pnpm run cache:collection\n`,
  );
};

// One source for the whole app: the "Kangaroo Point" collection. It carries
// all five artworks, so a single fetch at boot populates every sign's content
// and image target — no per-artwork URLs, no per-switch refetch.
export const CREATOR_DATA_SOURCE = COLLECTION_DATA_SOURCE;

// Manual content for non-creator items (scenes, models, etc.)
// Use targetName to match the 8th Wall target and override creator data (e.g. add custom scale)
export const MANUAL_CONTENT: Data[] = [];

// Boot from the locally cached collection, never the network. `creatorDataSource`
// is kept only as provenance — it is what the cache was pulled from, and what
// `pnpm run cache:collection` reads to refresh it.
configureCreatorContentStore({
  creatorDataSource: CREATOR_DATA_SOURCE,
  manualContent: MANUAL_CONTENT,
  injectedPayload: isBootable() ? getLocalCreatorPayload() : undefined,
});

/** Module-scope guard: the throw belongs in initializeCreatorData, not here. */
function isBootable(): boolean {
  try {
    assertCollectionCached();
    return true;
  } catch {
    return false;
  }
}

/**
 * Boot the collection into the content store.
 *
 * Everything comes from `src/collection/`, which is git-ignored and empty on a
 * fresh clone, so this fails fast and loudly when the cache has not been pulled
 * down. In dev we then verify the cached collection's artwork names still line
 * up with our explicit sign → id map, so a rename in Creator surfaces as a
 * warning rather than silently mis-binding a sign to the wrong content.
 */
export const initializeCreatorData = async (): Promise<void> => {
  assertCollectionCached();
  await initializeCreatorDataCore();
  if (import.meta.env.DEV) {
    const cache = getCacheProvenance();
    if (cache) {
      console.info(
        `[data] collection "${cache.name}" from cache, pulled ${cache.fetchedAt}\n` +
          `       source: ${cache.source}`,
      );
    }
    verifyCollectionMatchesSigns();
  }
};

const verifyCollectionMatchesSigns = (): void => {
  const payload = getCreatorPayload() as
    | { artworks?: { id: string; name?: string }[] }
    | null;
  const artworks = payload?.artworks ?? [];
  if (artworks.length === 0) return; // single-artwork payload / not yet loaded
  const nameById = new Map(artworks.map((a) => [a.id, a.name ?? ""]));

  (Object.keys(SIGN_TO_ARTWORK_ID) as SignKey[]).forEach((sign) => {
    const id = SIGN_TO_ARTWORK_ID[sign];
    const label = SIGN_LABEL[sign];
    if (!nameById.has(id)) {
      console.warn(`[data] sign "${sign}" → ${id} is not in the loaded collection.`);
    } else if (nameById.get(id) !== label) {
      console.warn(
        `[data] sign "${sign}" label "${label}" ≠ collection name "${nameById.get(id)}" (${id}).`,
      );
    }
  });
};

/**
 * The image target for the sign this route belongs to.
 *
 * Targets are packed offline into `src/collection/` and bundled, so there is no
 * canvas generation at boot and nothing is fetched. One target is returned, not
 * all five: the engine only ever tracks the boot sign, and the QR reveal flow
 * swaps another in from memory later (see `switchActiveArtwork`).
 */
export const getImageTargetDataAsync = async (): Promise<ImageTargetDescriptor[]> => {
  return [requireLocalTarget(getActiveArtworkConfig().artworkId)];
};

/**
 * Switch the active artwork mid-session (QR scan).
 *
 * The whole collection is already in memory — all five artworks were bundled
 * from the cache at build time, and `getContent()` already resolves any of them
 * by id. So there is NO network refetch and no content reload: we just mark the
 * scanned artwork active and hand back its bundled image-target descriptor.
 *
 * Returns the new image target descriptor(s) for the caller (main.app) to hand
 * to the running 8thwall engine. The AR session is NOT torn down.
 */
export const switchActiveArtwork = async (
  artworkId: string,
): Promise<ImageTargetDescriptor[]> => {
  setActiveArtworkById(artworkId);
  return [requireLocalTarget(artworkId)];
};
